import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  OnInit,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { GeolocationService } from '../../geolocation.service';

import { IconLoadingComponent } from '@pedix-workspace/angular-ui-icons';
import { NgClass, isPlatformBrowser } from '@angular/common';
import { ModalFormComponent } from '@pedix-workspace/angular-ui-modal';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { StadiaMapsComponent } from '../stadia-maps/stadia-maps.component';
import { StadiaMapsMarkerComponent } from '../stadia-maps-marker/stadia-maps-marker.component';
import { cloneDeep, isEqual } from 'lodash-es';
import { StadiaLatLng } from '../stadia-maps.types';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import { STADIA_MAPS_SERVICE_API_KEY } from '../stadia-maps.tokens';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'pxw-stadia-maps-location-popup',
  templateUrl: './stadia-maps-location-popup.component.html',
  styleUrls: ['./stadia-maps-location-popup.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    IconLoadingComponent,
    ModalFormComponent,
    ButtonComponent,
    StadiaMapsComponent,
    StadiaMapsMarkerComponent,
    AlertComponent,
  ],
})
export class StadiaMapsLocationPopupComponent implements OnInit {
  private apiKey = inject(STADIA_MAPS_SERVICE_API_KEY, { optional: true });
  private platformId = inject(PLATFORM_ID);
  private geolocationService = inject(GeolocationService);

  readonly modalForm = viewChild.required<ModalFormComponent>('modalForm');

  // Input signals
  readonly title = input.required<string>();
  readonly readyText = input.required<string>();
  readonly geolocationErrorText = input.required<string>();
  readonly mapInstructions = input<string[]>();
  readonly isOpen = model<boolean>(false);
  readonly defaultCenter = input.required<StadiaLatLng>();

  readonly stadiaMapsApiKey = model<string>();
  readonly marker = model<StadiaLatLng | null>(null);

  // Internal signals
  readonly markerCopy = signal<StadiaLatLng | undefined>(undefined);
  readonly loadingLocation = signal(false);
  readonly center = signal<StadiaLatLng>({ lat: -10.2335042, lng: -84.1720247 });
  readonly zoom = signal(4);
  readonly savedGeolocation = signal<StadiaLatLng | undefined>(undefined);
  readonly displayInstructions = signal(true);

  // Computed signals
  readonly isDirty = computed(() => !isEqual(this.marker(), this.markerCopy()));

  readonly mapOptions = {
    fullscreenControl: false,
    mapTypeControl: false,
    streetViewControl: false,
  };

  constructor() {
    toObservable(this.isOpen).subscribe(isOpen => {
      if (isPlatformBrowser(this.platformId) && isOpen) {
        this.onDisplayToggled();
      }
    });
  }

  ngOnInit(): void {
    if (!this.stadiaMapsApiKey() && this.apiKey) {
      this.stadiaMapsApiKey.set(this.apiKey);
    }
  }

  onDisplayToggled() {
    this.modalForm()?.open(true);
    this.markerCopy.set(undefined);

    if (this.marker()) {
      this.setDefaultsForGeolocation(this.marker()!);
    } else if (this.savedGeolocation()) {
      this.setDefaultsForGeolocation(this.savedGeolocation()!);
    } else {
      if (this.defaultCenter()) {
        this.center.set(this.defaultCenter());
        this.zoom.set(12);
      }

      // Additional, we ask permissions to get more accurate location
      this.geolocate();
    }
  }

  onReady() {
    const markerCopy = this.markerCopy();
    if (markerCopy) {
      this.marker.set(markerCopy);
    }
    this.modalForm()?.close();
  }

  onModalOpenToggle(isOpen: boolean) {
    this.isOpen.set(isOpen);
  }

  onMapClick(latLng: StadiaLatLng) {
    this.markerCopy.set(latLng);
  }

  onMarkerDragend(latLng: StadiaLatLng) {
    this.markerCopy.set(latLng);
  }

  async geolocate() {
    if (!navigator.geolocation) {
      return;
    }
    this.loadingLocation.set(true);

    try {
      const geolocationResult = await this.geolocationService.geolocate();

      if (geolocationResult) {
        this.savedGeolocation.set(geolocationResult);
        this.setDefaultsForGeolocation(geolocationResult);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.loadingLocation.set(false);
    }
  }

  setDefaultsForGeolocation(geolocation: StadiaLatLng) {
    this.center.set(geolocation);
    this.zoom.set(17);
    this.markerCopy.set(cloneDeep(geolocation));
  }

  onGeolocationResult(geolocation: StadiaLatLng) {
    this.setDefaultsForGeolocation(geolocation);
  }
}
